import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatUsageCount,
  formatUsageMinutes,
  normalizeUsageMinutes,
} from '../../../lib/utils/formatUsage';

const UsageMetric = ({ title, current, total, unit, color = 'primary', footnote = null }) => {
  const { t } = useTranslation();
  const isMinutes = unit === 'minutes';
  const unitLabel = isMinutes ? t('dashboard.unitMinutes') : t('dashboard.unitCandidates');

  const displayCurrent = isMinutes
    ? normalizeUsageMinutes(current, total)
    : Math.max(0, Number(current) || 0);
  const displayTotal = isMinutes
    ? normalizeUsageMinutes(total, total)
    : Math.max(0, Number(total) || 0);
  const displayRemaining = Math.max(displayTotal - displayCurrent, 0);
  const percentage = displayTotal > 0
    ? Math.round((displayCurrent / displayTotal) * 100)
    : 0;

  const formatValue = (value) => (
    isMinutes ? formatUsageMinutes(value) : formatUsageCount(value)
  );

  const getColorClasses = () => {
    switch (color) {
      case 'success':
        return 'bg-success text-success-foreground';
      case 'warning':
        return 'bg-warning text-warning-foreground';
      case 'error':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-primary text-primary-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm md:text-base font-medium text-foreground">{title}</h3>
        <span className="text-xs md:text-sm text-muted-foreground">
          {formatValue(displayCurrent)} / {formatValue(displayTotal)} {unitLabel}
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-2 md:h-3 mb-2">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getColorClasses()}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs md:text-sm">
        <span className="text-muted-foreground">{t('dashboard.percentUsed', { percent: percentage })}</span>
        <span className="text-muted-foreground">
          {t('dashboard.unitRemaining', { value: formatValue(displayRemaining), unit: unitLabel })}
        </span>
      </div>
      {footnote ? (
        <div className="mt-3 border-t border-border pt-3 text-xs md:text-sm text-muted-foreground">
          {footnote}
        </div>
      ) : null}
    </div>
  );
};

export default UsageMetric;
